/**
 * Aggregates last blocks from different networks
 */
import { IDexTrade, ITicker } from '@kattana-io/common';
import CentrifugeProto from 'centrifuge/build/protobuf';
import { Centrifuge, Subscription } from 'centrifuge';
import eventEmitter from './eventEmitter';
import {
  getLiveTradesEventName,
  getLiveCandlesEventName,
  getLastTradeEventName,
  getLastCexPriceEventName,
} from '../helpers/events-builder';
import { BackendCandlesObj } from '../components/TradingViewChart/datafeeds/DexDatafeed';
import cron from 'cron';
import {
  decodeProtoCandle,
  decodeProtoLastTrade,
  decodeProtoCexPriceUpdate,
  decodeProtoTrade,
  decodeCexBalanceUpdate,
  decodeCexOrderUpdate,
} from './proto-load';
import bigInt from 'big-integer';
import { base64ToHex } from '../components/Trading/helpers/elrondConverters';
import { ICEXOrderUpdate } from '../reducers/cex_orders';
import { IExtendedCEXBalanceItem } from '../reducers/cex_trading';

interface ICallbacksProps {
  newpairs: (message: string) => void;
  priceAlerts: (data: any) => void;
  limitOrders: (data: any) => void;
  updateCexBalance: (data: IExtendedCEXBalanceItem) => void;
  updateCexOrder: (data: ICEXOrderUpdate) => void;
}

export interface IGasState {
  FastGasPrice: string;
  LastBlock: string;
  ProposeGasPrice: string;
  SafeGasPrice: string;
}
interface ITradeData {
  amountIn: string; // "1002888007142555399"
  amountOut: string; // "1792722382"
  blocknumber: number; // 12000786
  date: string; // "2021-03-08 23:15:01"
  pair: string; // "0x0d4a11d5EEaaC28EC3F61d100daF4d40471f1852"
  price: string; // 1787.5599
  priceUSD: string;
  protocol: string; // "uniswapV2"
  tx: string; // "0xdda30da43b4f583d2f6c75e7797327a9b889cca16657d400c20462f3632ee79e"
  wallet: string; // "0x000000000000000000000000f06371cfecd87065ec3aaf792252fe02305f0cb2"
  timestamp: number;
  bot: boolean;
  buy: boolean;
  inch: boolean;
}

interface IMessageTradePayload {
  data: IDexTrade;
}

export interface ILastTrade {
  pair: string;
  is_buy: boolean;
  price_a: number;
  price_a_usd: number;
  price_b: number;
  price_b_usd: number;
  volume_a: string;
  volume_b: string;
  volume_usd: number;
}

export interface ILastCexPrice {
  tickerId: number;
  price: number;
  base_volume: number;
  quote_volume: number;
}

const dispatchNewEventForTrades = (
  trade: IDexTrade,
  chain: string,
  address: string
) => {
  const eventName = getLiveTradesEventName(chain, address);
  eventEmitter.emit(eventName, { trade });
};

const dispatchNewEventForCandles = (
  candle: any,
  chain: string,
  pair: string
) => {
  const eventName = getLiveCandlesEventName(chain, pair);
  eventEmitter.emit(eventName, { pair, candle });
};

const dispatchNewEventForLastTrade = (
  trade: ILastTrade,
  chain: string,
  address: string
) => {
  const eventName = getLastTradeEventName(chain, address);
  eventEmitter.emit(eventName, { trade });
};

const dispatchNewEventForLastCexPrice = (
  lastPrice: ILastCexPrice,
  exchange: string,
  id: number
) => {
  const eventName = getLastCexPriceEventName(exchange, id);
  eventEmitter.emit(eventName, lastPrice);
};

class BlocksSubscription {
  interval: any;
  callbacks: ICallbacksProps;
  centrifugeProto: CentrifugeProto;
  centrifuge: Centrifuge;
  tradeSubscriptions: { [hash: string]: { count: number; sub?: Subscription } };
  candleSubscriptions: {
    [hash: string]: { count: number; sub: Subscription; job: cron.CronJob };
  };
  lastTradeSubscriptions: {
    [hash: string]: { count: number; sub?: Subscription };
  };
  lastCEXPriceSubscriptions: {
    [hash: string]: { count: number; sub?: Subscription };
  };
  priceAlertSubscription?: Subscription;
  limitOrdersSubscription?: Subscription;
  primaryTradesSubscription?: Subscription;
  cexBalancesSubscription?: Subscription;
  cexOrdersSubscription: any;

  constructor(callbacks: ICallbacksProps) {
    this.callbacks = callbacks;
    this.centrifuge = this.onConnectWs();
    this.centrifugeProto = this.connectProto();
    this.tradeSubscriptions = {};
    this.candleSubscriptions = {};
    this.lastTradeSubscriptions = {};
    this.lastCEXPriceSubscriptions = {};
  }

  private connectProto = () => {
    const client = new CentrifugeProto(
      process.env.REACT_APP_PROTO_HOST ||
        'wss://orca.kattana.trade/c/w?format=proto',
      {
        protocol: 'protobuf',
      }
    );
    client.connect();
    client.on('disconnected', (disconnectCtx) => {
      console.error('disconnected', disconnectCtx);
    });

    return client;
  };

  private onConnectWs = () => {
    const client = new Centrifuge(
      process.env.REACT_APP_WS_HOST || 'wss://orca.kattana.trade/c/w',
      {
        websocket: WebSocket,
      }
    );
    client.connect();
    client.on('disconnected', (disconnectCtx) => {
      console.error('disconnected', disconnectCtx);
    });

    return client;
  };

  private createPriceAlertSubscription(wallet: string): Subscription {
    const alertsSubscription = `notifications/${wallet}`;

    const sub = this.centrifuge.newSubscription(alertsSubscription);
    sub.on('publication', (message: any) => {
      this.callbacks.priceAlerts(message.data);
    });

    sub.subscribe();

    return sub;
  }

  private createLimitOrdersSubscription(wallet: string): Subscription {
    const ordersSubscription = `limit_orders/${wallet}`;

    const sub = this.centrifuge.newSubscription(ordersSubscription);

    sub.on('publication', (message: any) => {
      this.callbacks.limitOrders(message.data);
    });

    sub.subscribe();

    return sub;
  }

  /**
   * Create and return subscription object
   * @param protocol
   * @param address
   * @param exchange
   * @param base
   * @param quote
   * @returns
   */
  private createTradeSubscription = (
    chain: string,
    address: string
  ): Subscription => {
    const tradesSubscription = `bi:trades/${chain}/${address}`;

    const sub = this.centrifugeProto.newSubscription(tradesSubscription);

    sub.on('publication', (message: any) => {
      const data = decodeProtoTrade(message.data);

      /**
       * technical debt:
       * This is the only point to receive live trades
       * So in order to sort this out and avoid this kind of convertation
       * we can do necessary preparation on the backend
       */

      const am0 = bigInt(base64ToHex(data.Amount0), 16).toString();
      const am1 = bigInt(base64ToHex(data.Amount1), 16).toString();
      const liveTrade: IDexTrade = {
        tx: data.Tx,
        date: new Date(Number(data.Date) * 1000),
        chain: data.Chain,
        blocknumber: Number(data.BlockNumber),
        pair: data.Pair,
        amount0: am0,
        amount1: am1,
        buy: data.Buy ? 1 : 0,
        bot: data.Bot ? 1 : 0,
        wallet: data.Wallet,
        order: data.Order,
        value_usd: data.ValueUSD,
        timestamp: Number(data.Date),
        price_a: Number(data.PriceA),
        price_a_usd: Number(data.PriceAUSD),
        price_b: Number(data.PriceB),
        price_b_usd: Number(data.PriceBUSD),
      };

      dispatchNewEventForTrades(liveTrade, chain, address);
    });

    sub.subscribe();

    return sub;
  };

  private createCandleSubscription = (
    chain: string,
    address: string
  ): { sub: Subscription; job: cron.CronJob } => {
    const candleSubscription = `bi:candles:${chain}/${address}`;

    const sub = this.centrifugeProto.newSubscription(candleSubscription);

    sub.on('publication', ({ data }: any) => {
      // const decoder = new TextDecoder('utf-8');
      // const res = decoder.decode(data);

      // console.log({ data });
      const res = decodeProtoCandle(data);
      // console.log({ res });

      dispatchNewEventForCandles(res, chain, address);
    });

    sub.subscribe();

    const job = new cron.CronJob('2 * * * * *', () => {
      // special delay in 2 seconds to give a few seconds
      const emptyCandle: BackendCandlesObj = {
        // for previous candle to load trades from blocks with little delay
        Candle: [],
        Volume: {} as any,
        empty: true,
      };

      dispatchNewEventForCandles(emptyCandle, chain, address);
    });
    job.start();

    return { sub, job };
  };

  /**
   * Create and return subscription object
   * @param chain
   * @param address
   * @returns
   */
  private createLastTradeSubscription = (
    chain: string,
    address: string
  ): Subscription => {
    const lastTradeSubscription = `bi:last_trade:${chain}/${address}`;

    const sub = this.centrifugeProto.newSubscription(lastTradeSubscription);

    sub.on('publication', (message: any) => {
      const data = decodeProtoLastTrade(message.data);

      const volume_a = bigInt(base64ToHex(data.VolumeA), 16).toString();
      const volume_b = bigInt(base64ToHex(data.VolumeB), 16).toString();
      const lastTrade: ILastTrade = {
        pair: data.Pair,
        is_buy: data.IsBuy,
        price_a: Number(data.PriceA),
        price_a_usd: Number(data.PriceAUSD),
        price_b: Number(data.PriceB),
        price_b_usd: Number(data.PriceBUSD),
        volume_a,
        volume_b,
        volume_usd: Number(data.VolumeUSD),
      };

      dispatchNewEventForLastTrade(lastTrade, chain, address);
    });

    sub.subscribe();

    return sub;
  };

  /**
   * Create and return subscription object
   * @param id
   * @param exchange
   * @returns
   */
  private createLastCexPriceSubscription = (
    exchange: string,
    id: number
  ): Subscription => {
    const lastCexPriceSubscription = `bi:cex_price_${id}`;

    const sub = this.centrifugeProto.newSubscription(lastCexPriceSubscription);

    sub.on('publication', (message: any) => {
      const update = decodeProtoCexPriceUpdate(message.data);

      const lastPrice = {
        tickerId: id,
        price: parseFloat(update.Price),
        base_volume: update.BaseVolume,
        quote_volume: update.QuoteVolume,
      };

      dispatchNewEventForLastCexPrice(lastPrice, exchange, id);
    });

    sub.subscribe();

    return sub;
  };

  /**
   * subscribeToPriceAlerts
   */
  public subscribeToPriceAlerts(wallet: string) {
    if (this.priceAlertSubscription) {
      this.unsubscribeFromPriceAlerts();
    }

    const sub = this.createPriceAlertSubscription(wallet);
    this.priceAlertSubscription = sub;
  }

  /**
   * subscribeToLimitOrders
   */
  public subscribeToLimitOrders(wallet: string) {
    if (this.limitOrdersSubscription) {
      this.unsubscribeFromLimitOrders();
    }

    const sub = this.createLimitOrdersSubscription(wallet);
    this.limitOrdersSubscription = sub;
  }

  /**
   * subscribeToPrimaryTrades
   */
  public subscribeToPrimaryTrades(chain: string, address: string) {
    if (this.primaryTradesSubscription) {
      this.unsubscribeFromPrimaryTrades();
    }

    const sub = this.createTradeSubscription(chain, address);
    this.primaryTradesSubscription = sub;
  }

  /**
   * unsubscribeFromPriceAlerts
   */
  public unsubscribeFromPriceAlerts() {
    if (this.priceAlertSubscription) {
      this.priceAlertSubscription.unsubscribe();
      this.centrifuge.removeSubscription(this.priceAlertSubscription);
      delete this.priceAlertSubscription;
    }
  }

  /**
   * unsubscribeFromLimitOrders
   */
  public unsubscribeFromLimitOrders() {
    if (this.limitOrdersSubscription) {
      this.limitOrdersSubscription.unsubscribe();
      this.centrifuge.removeSubscription(this.limitOrdersSubscription);
      delete this.limitOrdersSubscription;
    }
  }

  /**
   * unsubscribeFromPrimaryTrades
   */
  public unsubscribeFromPrimaryTrades() {
    if (this.primaryTradesSubscription) {
      this.primaryTradesSubscription.unsubscribe();
      this.centrifugeProto.removeSubscription(this.primaryTradesSubscription);
      delete this.primaryTradesSubscription;
    }
  }

  /**
   * Unsubscribe from trades
   */
  public unsubscribeFromLiveTrades(curHash: string) {
    if (this.tradeSubscriptions[curHash].count > 1) {
      this.tradeSubscriptions[curHash].count -= 1;
    } else {
      const { sub } = this.tradeSubscriptions[curHash];
      if (sub) {
        sub.unsubscribe();
        this.centrifugeProto.removeSubscription(sub);
      }
      delete this.tradeSubscriptions[curHash];
    }
  }
  /**
   * Subscribe to trades
   */
  public subscribeToLiveTrades(curHash: string, ticker: ITicker) {
    if (!this.tradeSubscriptions[curHash]) {
      const { address, chain } = ticker;
      const sub = this.createTradeSubscription(chain, address);
      this.tradeSubscriptions[curHash] = { count: 1, sub };
    } else {
      this.tradeSubscriptions[curHash].count += 1;
    }
  }

  /**
   * Subscribe to candles for chart
   */
  public subscribeToLiveCandles(curHash: string, ticker: ITicker) {
    if (!this.candleSubscriptions[curHash]) {
      const { address, chain } = ticker;
      const { sub, job } = this.createCandleSubscription(chain, address);
      this.candleSubscriptions[curHash] = { count: 1, sub, job };
    } else {
      this.candleSubscriptions[curHash].count += 1;
    }
  }

  public unsubscribeFromLiveCandles(curHash: string) {
    if (this.candleSubscriptions[curHash].count > 1) {
      this.candleSubscriptions[curHash].count -= 1;
    } else {
      const { sub, job } = this.candleSubscriptions[curHash];
      sub.unsubscribe();
      this.centrifugeProto.removeSubscription(sub);
      job.stop();
      delete this.candleSubscriptions[curHash];
    }
  }

  /**
   * Unsubscribe from trades
   */
  public unsubscribeFromLastTrade(curHash: string) {
    if (this.lastTradeSubscriptions[curHash].count > 1) {
      this.lastTradeSubscriptions[curHash].count -= 1;
    } else {
      const { sub } = this.lastTradeSubscriptions[curHash];
      if (sub) {
        sub.unsubscribe();
        this.centrifugeProto.removeSubscription(sub);
      }
      delete this.lastTradeSubscriptions[curHash];
    }
  }
  /**
   * Subscribe to trades
   */
  public subscribeToLastTrade(curHash: string, ticker: ITicker) {
    if (!this.lastTradeSubscriptions[curHash]) {
      const { address, chain } = ticker;
      const sub = this.createLastTradeSubscription(chain, address);
      this.lastTradeSubscriptions[curHash] = { count: 1, sub };
    } else {
      this.lastTradeSubscriptions[curHash].count += 1;
    }
  }

  /**
   * Unsubscribe from trades
   */
  public unsubscribeFromLastCexPrice(curHash: string) {
    if (this.lastCEXPriceSubscriptions[curHash].count > 1) {
      this.lastCEXPriceSubscriptions[curHash].count -= 1;
    } else {
      const { sub } = this.lastCEXPriceSubscriptions[curHash];
      if (sub) {
        sub.unsubscribe();
        this.centrifugeProto.removeSubscription(sub);
      }
      delete this.lastCEXPriceSubscriptions[curHash];
    }
  }
  /**
   * Subscribe to trades
   */
  public subscribeToLastCEXPrice(
    curHash: string,
    exchange: string,
    id: number
  ) {
    if (!this.lastCEXPriceSubscriptions[curHash]) {
      const sub = this.createLastCexPriceSubscription(exchange, id);
      this.lastCEXPriceSubscriptions[curHash] = { count: 1, sub };
    } else {
      this.lastCEXPriceSubscriptions[curHash].count += 1;
    }
  }

  /**
   * CEX Live balances
   * @param wallet
   */
  public subscribeToCEXBalances(wallet: string) {
    if (this.cexBalancesSubscription) {
      this.unsubscribeFromCEXBalances();
    }
    const name = `bi:cex_balance_${wallet}`;
    const sub = this.centrifugeProto.newSubscription(name);
    sub.on('publication', (message: any) => {
      this.callbacks.updateCexBalance(decodeCexBalanceUpdate(message.data));
    });

    sub.subscribe();
    this.cexBalancesSubscription = sub;
  }

  /**
   * Unsubscribe from cex balances
   */
  public unsubscribeFromCEXBalances() {
    if (this.cexBalancesSubscription) {
      this.cexBalancesSubscription.unsubscribe();
      this.centrifugeProto.removeSubscription(this.cexBalancesSubscription);
      this.cexBalancesSubscription = undefined;
    }
  }

  /**
   * Order event subscriptions
   * @param wallet
   */
  subscribeToCEXOrders(wallet: any) {
    if (this.cexOrdersSubscription) {
      this.unsubscribeFromCEXOrders();
    }
    const name = `bi:cex_order_${wallet}`;
    const sub = this.centrifugeProto.newSubscription(name);
    sub.on('publication', (message: any) => {
      this.callbacks.updateCexOrder(decodeCexOrderUpdate(message.data));
    });

    sub.subscribe();
    this.cexOrdersSubscription = sub;
  }

  public unsubscribeFromCEXOrders() {
    if (this.cexOrdersSubscription) {
      this.cexOrdersSubscription.unsubscribe();
      this.centrifugeProto.removeSubscription(this.cexOrdersSubscription);
      this.cexOrdersSubscription = undefined;
    }
  }
}

export default BlocksSubscription;
